<script setup lang="ts">
import { onBeforeMount, ref, watch } from "vue";

const props = defineProps({
  valor: {
    type: Number,
    required: true
  },
  precision: {
    type: Number,
    required: true
  },
  acceptNegative: {
    type: Boolean,
    required: false
  },
  isInteger: {
    type: Boolean,
    required: false
  },
  autoSeleccionar: {
    type: Boolean,
    required: false
  },
  align: {
    type: String,
    required: false,
    default: "right"
  }
});

const valorInput = ref<string>("");
const presionoParaDecimal = ref(false);

const myNumberFormat = ref<HTMLInputElement | null>(null);

const emit = defineEmits<{
  (e: "change", valor: number): void;
}>();

function countOccurrences(targetString: string, charToCount: string) {
  let count = 0;
  for (let i = 0; i < targetString.length; i++) {
    if (targetString.charAt(i) === charToCount) {
      count++;
    }
  }
  return count;
}

const onPaste = (e: ClipboardEvent) => {
  const input = e.clipboardData?.getData("text/plain") ?? "";
  let valorInputEntero: number = 0;
  let valorInputDecimal: number = 0;
  const cantPuntos = countOccurrences(input, ".");
  const cantComas = countOccurrences(input, ",");

  if (cantPuntos > 0 && cantComas > 0) {
    presionoParaDecimal.value = true;
    const numberFloat = parseFloat(input.replaceAll(".", "").replace(",", "."));
    valorInputEntero = parseInt(numberFloat.toString());
    valorInputDecimal = parseInt(
      ((numberFloat - valorInputEntero) * 10 ** props.precision).toFixed(0)
    );

    const formattedInt = valorInputEntero
      .toLocaleString("en-US")
      .replaceAll(",", ".");

    valorInput.value = `${formattedInt},${valorInputDecimal}`;
  } else if (cantPuntos > 0 || cantComas > 0) {
    let numberFloat: number = 0;
    if (cantPuntos > 0) {
      numberFloat = parseFloat(input);
    } else if (cantComas == 1) {
      numberFloat = parseFloat(input.replaceAll(",", "."));
    } else {
      numberFloat = parseFloat(input.replaceAll(",", ""));
    }

    presionoParaDecimal.value = true;
    valorInputEntero = parseInt(numberFloat.toString());
    valorInputDecimal = parseInt(
      ((numberFloat - valorInputEntero) * 10 ** props.precision).toFixed(0)
    );

    const formattedInt = valorInputEntero
      .toLocaleString("en-US")
      .replaceAll(",", ".");

    valorInput.value = `${formattedInt},${valorInputDecimal}`;
  } else if (!isNaN(parseInt(input))) {
    const formattedInt = parseInt(input)
      .toLocaleString("en-US")
      .replaceAll(",", ".");
    valorInput.value = formattedInt;
  }

  e.preventDefault();
};

const onKeyUp = (e: KeyboardEvent) => {
  if (e.key == ".") {
    if (valorInput.value.includes(".")) {
      e.preventDefault();
    }
  } else {
    e.preventDefault();
  }
};

const onBlur = () => {
  let numero = "";
  if (valorInput.value.includes(",")) {
    numero = valorInput.value.replaceAll(".", "").replace(",", ".");
  } else {
    const cantPuntos = countOccurrences(valorInput.value, ".");
    if (cantPuntos == 0) {
      numero = valorInput.value;
    } else if (cantPuntos > 1) {
      numero = valorInput.value.replaceAll(".", "");
    } else {
      // Incluye solo un punto, no sé si es decimal o separador de miles
      // Si hay 2 caracteres a la derecha, asumo que es separador decimal
      const parts = valorInput.value.split(".");
      if (parts[1].length == 2 || parts[1].length == props.precision) {
        presionoParaDecimal.value = true;
        numero = valorInput.value;
      } else if (parts[1].length == 1) {
        presionoParaDecimal.value = true;
        numero = valorInput.value;
      } else {
        numero = valorInput.value.replaceAll(".", "");
      }
    }
  }
  const numberFloat = parseFloat(numero);
  if (isNaN(numberFloat)) {
    emit("change", 0);
  } else {
    emit("change", numberFloat);
  }
};

const checkKeyDown = (e: any) => {
  if (e.key == ".") {
    if (valorInput.value.includes(".")) {
      e.preventDefault();
    }
  }
  if (e.key == "-" && props.acceptNegative == false) {
    e.preventDefault();
  }

  const caracteresPermitidos = [
    ".",
    "-",
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9"
  ];
  const allowedSpecialKeys = [
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Backspace",
    "Delete",
    "Paste",
    "Meta",
    "Tab"
  ];
  const isSpecialKey = allowedSpecialKeys.includes(e.key);
  const isCharacterAllowed = caracteresPermitidos.includes(e.key);
  const isSelectAll =
    (e.metaKey || e.ctrlKey) &&
    (e.key === "a" || e.key === "v" || e.key === "c");
  if (!isCharacterAllowed && !isSpecialKey && !isSelectAll) {
    e.preventDefault();
    return;
  }
};

watch(
  () => props.valor,
  newValue => {
    mostrarDesdeProp(newValue, props.precision ? props.precision : 0);
  }
);

onBeforeMount(() => {
  mostrarDesdeProp(props.valor, props.precision ? props.precision : 0);
});

const mostrarDesdeProp = (valor: number, precision: number) => {
  const newValue = valor;
  const parts = newValue.toString().split(".");
  let valorInputEntero = parseInt(parts[0]);
  let valorInputDecimal = parseInt(
    ((newValue - valorInputEntero) * 10 ** precision).toFixed(0)
  );
  if (valorInputDecimal < 0) {
    valorInputDecimal = valorInputDecimal * -1;
  }

  if (valorInputDecimal > 0) {
    presionoParaDecimal.value = true;
  } else {
    presionoParaDecimal.value = false;
  }
  let valorInputDecimalStr = "";

  if (presionoParaDecimal.value) {
    if (props.precision == 2) {
      if (valorInputDecimal < 10) {
        valorInputDecimalStr = "0".concat(valorInputDecimal.toString());
      } else {
        valorInputDecimalStr = valorInputDecimal.toString();
      }
    } else if (props.precision == 3) {
      if (valorInputDecimal < 10) {
        valorInputDecimalStr = "00".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 100) {
        valorInputDecimalStr = "0".concat(valorInputDecimal.toString());
      } else {
        valorInputDecimalStr = valorInputDecimal.toString();
      }
    } else if (props.precision == 4) {
      if (valorInputDecimal < 10) {
        valorInputDecimalStr = "000".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 100) {
        valorInputDecimalStr = "00".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 1000) {
        valorInputDecimalStr = "0".concat(valorInputDecimal.toString());
      } else {
        valorInputDecimalStr = valorInputDecimal.toString();
      }
    } else if (props.precision == 5) {
      if (valorInputDecimal < 10) {
        valorInputDecimalStr = "0000".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 100) {
        valorInputDecimalStr = "000".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 1000) {
        valorInputDecimalStr = "00".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 10000) {
        valorInputDecimalStr = "0".concat(valorInputDecimal.toString());
      } else {
        valorInputDecimalStr = valorInputDecimal.toString();
      }
    } else if (props.precision == 6) {
      if (valorInputDecimal < 10) {
        valorInputDecimalStr = "00000".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 100) {
        valorInputDecimalStr = "0000".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 1000) {
        valorInputDecimalStr = "000".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 10000) {
        valorInputDecimalStr = "00".concat(valorInputDecimal.toString());
      } else if (valorInputDecimal < 100000) {
        valorInputDecimalStr = "0".concat(valorInputDecimal.toString());
      } else {
        valorInputDecimalStr = valorInputDecimal.toString();
      }
    }
  } else {
    valorInputDecimal = 0;
  }
  const formattedInt = valorInputEntero
    .toLocaleString("en-US")
    .replaceAll(",", ".");

  if (presionoParaDecimal.value) {
    valorInput.value = `${formattedInt},${valorInputDecimalStr}`;
  } else {
    valorInput.value = formattedInt;
  }
};

const seleccionarInput = () => {
  // Tengo que formatear para escribir
  let numero = "";
  if (valorInput.value.includes(",")) {
    numero = valorInput.value.replaceAll(".", "").replace(",", ".");
  } else {
    const cantPuntos = countOccurrences(valorInput.value, ".");
    if (cantPuntos == 0) {
      numero = valorInput.value;
    } else if (cantPuntos > 1) {
      numero = valorInput.value.replaceAll(".", "");
    } else {
      // Incluye solo un punto, no sé si es decimal o separador de miles
      // Si hay 2 caracteres a la derecha, asumo que es separador decimal
      const parts = valorInput.value.split(".");
      if (parts[1].length == 2 || parts[1].length == props.precision) {
        presionoParaDecimal.value = true;
        numero = valorInput.value;
      } else if (parts[1].length == 1) {
        presionoParaDecimal.value = true;
        numero = valorInput.value;
      } else {
        numero = valorInput.value.replaceAll(".", "");
      }
    }
  }
  valorInput.value = numero;

  const shouldAutoSelect =
    props.autoSeleccionar || props.valor === 0 || valorInput.value === "0";
  if (shouldAutoSelect && myNumberFormat?.value != null) {
    // Esperar que el input se repinte con el nuevo valor.
    requestAnimationFrame(() => myNumberFormat?.value?.select());
  }
};
</script>

<template>
  <input
    ref="myNumberFormat"
    :class="[
      'disabled:bg-slate-100 disabled:cursor-not-allowed dark:disabled:bg-darkmode-800/50 dark:disabled:border-transparent [&[readonly]]:bg-slate-100 [&[readonly]]:cursor-not-allowed [&[readonly]]:dark:bg-darkmode-800/50 [&[readonly]]:dark:border-transparent transition duration-200 ease-in-out w-full text-sm border-slate-200 shadow-sm rounded-md placeholder:text-slate-300/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 dark:bg-darkmode-800 dark:border-transparent dark:focus:ring-slate-700 dark:focus:ring-opacity-50 dark:placeholder:text-slate-100',
      align === 'left' ? 'text-left' : 'text-right'
    ]"
    type="text"
    v-model="valorInput"
    @click="seleccionarInput"
    @keydown="checkKeyDown"
    @keyup="onKeyUp"
    @paste="onPaste"
    @blur="onBlur"
  />
</template>
